function fallbackAmount(amountMinor: number, currency: string, whole: boolean): string {
  const amount = amountMinor / 100;
  const value = whole
    ? Math.round(amount).toString()
    : amount.toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
  const symbol = currency.toUpperCase() === "USD" ? "$" : `${currency.toUpperCase()} `;
  return `${symbol}${value}`;
}

/** Compact customer-facing currency: `$5`, `$4.50`, `$0` rather than `US$5.00`. */
export function formatBillingPrice(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    return fallbackAmount(amountMinor, currency, false);
  }
}

/**
 * Like {@link formatBillingPrice} but never a lone trailing digit: an amount with cents renders
 * them both. "$2.5" reads as a typo where "$2.50" reads as a price, and a difference between two
 * plans lands on cents far more often than a plan price does.
 */
export function formatBillingPriceExact(amountMinor: number, currency: string): string {
  const hasCents = amountMinor % 100 !== 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(amountMinor / 100);
  } catch {
    // Not fallbackAmount(): it trims a trailing zero, which is the very thing this variant exists
    // to keep.
    const symbol = currency.toUpperCase() === "USD" ? "$" : `${currency.toUpperCase()} `;
    return `${symbol}${(amountMinor / 100).toFixed(hasCents ? 2 : 0)}`;
  }
}

/** Whole-unit variant used by prominent pricing figures. */
export function formatBillingPriceWhole(amountMinor: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.round(amountMinor / 100));
  } catch {
    return fallbackAmount(amountMinor, currency, true);
  }
}

/**
 * Same output as {@link formatBillingPriceWhole}, split so the currency symbol can be styled
 * apart from the digits. Display prices set the symbol at a large size and weight, where the
 * gap it leaves before the number varies by digit — "$0" sits noticeably looser than "$4" —
 * and letter-spacing cannot even that out because it shifts both together.
 *
 * `symbolLeads` reports which side the symbol fell on, since not every locale prefixes it.
 */
export function splitBillingPriceWhole(
  amountMinor: number,
  currency: string,
): { symbol: string; amount: string; symbolLeads: boolean } {
  try {
    const parts = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(Math.round(amountMinor / 100));

    const symbolIndex = parts.findIndex((p) => p.type === "currency");
    if (symbolIndex === -1) {
      return { symbol: "", amount: parts.map((p) => p.value).join(""), symbolLeads: true };
    }
    const symbol = parts[symbolIndex].value;
    const amount = parts
      .filter((p) => p.type !== "currency")
      .map((p) => p.value)
      .join("")
      .trim();
    return { symbol, amount, symbolLeads: symbolIndex === 0 };
  } catch {
    const whole = fallbackAmount(amountMinor, currency, true);
    const match = /^([^\d-]*)(.*)$/.exec(whole);
    return { symbol: (match?.[1] ?? "").trim(), amount: (match?.[2] ?? whole).trim(), symbolLeads: true };
  }
}
